/**
 * Jean Meeus 알고리즘 기반 태양황경 계산.
 *
 * 출처: Jean Meeus, "Astronomical Algorithms", 2nd edition, Chapters 25-27.
 * 장동(nutation) 및 광행차(aberration) 보정을 포함한 시황경을 계산한다.
 */

const DEG_TO_RAD = Math.PI / 180;

/**
 * J2000.0 기원으로부터의 율리우스 세기를 계산한다.
 *
 * @param jd - 율리우스일
 * @returns J2000.0 기준 율리우스 세기
 */
export function julianCentury(jd: number): number {
  return (jd - 2451545.0) / 36525.0;
}

/**
 * 각도를 [0, 360) 범위로 정규화한다.
 *
 * @param angle - 각도 (도)
 * @returns 정규화된 각도 (도, [0, 360))
 */
export function normalizeAngle(angle: number): number {
  let result = angle % 360;
  if (result < 0) result += 360;
  return result;
}

/**
 * 태양의 기하학적 평균 황경(L0)을 계산한다.
 *
 * Meeus, Chapter 25.
 *
 * @param T - J2000.0 기준 율리우스 세기
 * @returns 기하학적 평균 황경 (도, [0, 360))
 */
function geometricMeanLongitude(T: number): number {
  return normalizeAngle(280.46646 + 36000.76983 * T + 0.0003032 * T * T);
}

/**
 * 태양의 평균 근점이각(M)을 계산한다.
 *
 * @param T - J2000.0 기준 율리우스 세기
 * @returns 평균 근점이각 (도, [0, 360))
 */
function meanAnomaly(T: number): number {
  return normalizeAngle(357.52911 + 35999.05029 * T - 0.0001537 * T * T);
}

/**
 * 태양의 중심차(C)를 계산한다.
 *
 * 지구 궤도의 이심률을 반영한다.
 *
 * @param T - J2000.0 기준 율리우스 세기
 * @param M - 평균 근점이각 (도)
 * @returns 중심차 (도)
 */
function equationOfCenter(T: number, M: number): number {
  const Mrad = M * DEG_TO_RAD;
  return (
    (1.914602 - 0.004817 * T - 0.000014 * T * T) * Math.sin(Mrad) +
    (0.019993 - 0.000101 * T) * Math.sin(2 * Mrad) +
    0.000289 * Math.sin(3 * Mrad)
  );
}

/**
 * 달 궤도의 승교점 황경(Omega)을 계산한다.
 *
 * 장동 보정에 사용한다.
 *
 * @param T - J2000.0 기준 율리우스 세기
 * @returns Omega (도, [0, 360))
 */
function moonAscendingNode(T: number): number {
  return normalizeAngle(125.04 - 1934.136 * T);
}

/**
 * 시태양황경을 계산한다 (장동 + 광행차 보정 포함).
 *
 * 지구에서 관측한 황도 좌표계 경도이다.
 *
 * @param jd - 율리우스일
 * @returns 태양황경 (도, [0, 360))
 */
export function solarLongitude(jd: number): number {
  const T = julianCentury(jd);
  const L0 = geometricMeanLongitude(T);
  const M = meanAnomaly(T);
  const C = equationOfCenter(T, M);

  // Sun's true longitude
  const trueLongitude = L0 + C;

  // Apparent longitude (nutation + aberration correction)
  const omega = moonAscendingNode(T);
  const apparent = trueLongitude - 0.00569 - 0.00478 * Math.sin(omega * DEG_TO_RAD);

  return normalizeAngle(apparent);
}

/**
 * 균시차(Equation of Time)를 계산한다.
 *
 * 균시차는 시태양시와 평균태양시의 차이로,
 * 지구 궤도의 이심률과 자전축 기울기에 의해 발생한다.
 *
 * 출처: NOAA Solar Calculator / Jean Meeus.
 *
 * @param jd - 율리우스일
 * @returns 균시차 (분 단위, 양수 = 해시계가 시계보다 빠름)
 */
export function equationOfTime(jd: number): number {
  const T = julianCentury(jd);
  const L0 = geometricMeanLongitude(T) * DEG_TO_RAD;
  const M = meanAnomaly(T) * DEG_TO_RAD;
  const e = 0.016708634 - 0.000042037 * T - 0.0000001267 * T * T;

  // Obliquity of the ecliptic
  const epsilon = (23.439291 - 0.0130042 * T - 0.00000016 * T * T) * DEG_TO_RAD;
  const y = Math.tan(epsilon / 2) ** 2;

  const eot =
    y * Math.sin(2 * L0) -
    2 * e * Math.sin(M) +
    4 * e * y * Math.sin(M) * Math.cos(2 * L0) -
    0.5 * y * y * Math.sin(4 * L0) -
    1.25 * e * e * Math.sin(2 * M);

  // Convert radians to minutes of time (1 radian ≈ 229.18 minutes)
  return eot * 229.18;
}
